import React from 'react'
import { Alert, Box, Stack } from '@mui/material'
import GameShell from './components/GameShell'
import QualificationPhase from './components/QualificationPhase'
import FinalPhase from './components/FinalPhase'
import ScorePanel from './components/ScorePanel'
import GameSummary from './components/GameSummary'
import BrandSelector from './components/BrandSelector'
import { fetchCarBrands } from './api/fetchCarBrands'
import { JUSTE_ENCHERE_CONFIG } from './config'
import { useJusteEnchereGame } from './hooks/useJusteEnchereGame'
import type { CarBrand } from './types'
import FinalIntro from './components/FinalIntro'
import GameBackdrop from './components/GameBackdrop'

type JusteEnchereGameProps = {
  running: boolean
}

const JusteEnchereGame = ({ running }: JusteEnchereGameProps) => {
  const [brands, setBrands] = React.useState<Array<CarBrand>>([])
  const [brandsLoading, setBrandsLoading] = React.useState(false)
  const [brandsError, setBrandsError] = React.useState<string | null>(null)
  const [selectedBrandId, setSelectedBrandId] = React.useState('')

  const selectedBrand = React.useMemo(
    () => brands.find((brand) => brand.id === selectedBrandId) ?? null,
    [brands, selectedBrandId],
  )

  const {
    phase,
    loading,
    statusMessage,
    error,
    qualification,
    finalRound,
    stats,
    lastDirection,
    qualificationFeedback,
    finalFeedback,
    finalCountdown,
    startGame,
    submitQualificationGuess,
    submitFinalGuess,
    resetGame,
    beginFinalCountdown,
  } = useJusteEnchereGame({ brand: selectedBrand?.name ?? null })

  React.useEffect(() => {
    let cancelled = false
    const loadBrands = async () => {
      setBrandsLoading(true)
      try {
        const list = await fetchCarBrands()
        if (!cancelled) {
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.log('Loaded car brands for Juste Enchère', { count: list.length })
          }
          setBrands(list)
          setBrandsError(null)
        }
      } catch (err) {
        if (!cancelled) {
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.error('Unable to load car brands for Juste Enchère', err)
          }
          setBrandsError('Impossible de charger la liste des marques')
        }
      } finally {
        if (!cancelled) {
          setBrandsLoading(false)
        }
      }
    }
    loadBrands()
    return () => {
      cancelled = true
    }
  }, [])

  React.useEffect(() => {
    if (!running) {
      resetGame()
    }
  }, [running, resetGame])

  const showEndScreen = phase === 'success' || phase === 'fail'
  const showQualification = !showEndScreen && phase === 'qualification'
  const showFinalIntro = !showEndScreen && phase === 'final_ready'
  const showFinal = !showEndScreen && phase === 'final'
  const revealPrice = phase === 'success' || phase === 'fail'
  const showBrandSelector = phase === 'idle'
  const showIntroMessage = phase === 'idle'

  const handleQuit = React.useCallback(() => {
    resetGame()
  }, [resetGame])

  const handleBrandChange = React.useCallback(
    (brandId: string) => {
      if (brandId === selectedBrandId) return
      setSelectedBrandId(brandId)
      resetGame()
    },
    [resetGame, selectedBrandId],
  )

  return (
    <Stack spacing={2} alignItems="center" sx={{ width: '100%' }}>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 3,
          justifyContent: 'center',
          alignItems: 'flex-start',
        }}
      >
        <GameBackdrop>
          {/* ✅ On crée un “stage” centré avec largeur max : ça supprime le grand vide central */}
          <Box
            sx={{
              width: '100%',
              maxWidth: 1400, // ajuste si besoin (1200/1360/1440)
              mx: 'auto',
              px: { xs: 1.5, md: 3 },
            }}
          >
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: { xs: 2, md: 3 },
                alignItems: 'flex-start',
                justifyContent: { xs: 'center', md: 'stretch' }, // pas de distribution “vide”
              }}
            >
              <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                {showEndScreen ? (
                  <GameSummary
                    phase={phase}
                    finalVehicle={finalRound.vehicle}
                    onQuit={handleQuit}
                  />
                ) : (
                  <GameShell
                    phase={phase}
                    loading={loading}
                    statusMessage={statusMessage}
                    onStart={startGame}
                    onReset={resetGame}
                  >
                    {showBrandSelector ? (
                      <Box sx={{ width: '100%', mb: 2, display: 'flex', justifyContent: 'center' }}>
                        <BrandSelector
                          options={brands}
                          value={selectedBrandId}
                          loading={brandsLoading}
                          onChange={handleBrandChange}
                        />
                      </Box>
                    ) : null}

                    {showQualification ? (
                      <QualificationPhase
                        vehicle={qualification.vehicle}
                        referencePrice={qualification.referencePrice}
                        remainingMs={qualification.remainingMs}
                        totalDurationMs={JUSTE_ENCHERE_CONFIG.qualification.totalDurationMs}
                        successes={qualification.successes}
                        required={JUSTE_ENCHERE_CONFIG.qualification.requiredSuccesses}
                        onGuess={submitQualificationGuess}
                        feedback={qualificationFeedback}
                      />
                    ) : null}

                    {showFinalIntro ? (
                      <FinalIntro countdown={finalCountdown} onStart={beginFinalCountdown} />
                    ) : null}

                    {showFinal ? (
                      <FinalPhase
                        vehicle={finalRound.vehicle}
                        remainingMs={finalRound.remainingMs}
                        totalDurationMs={JUSTE_ENCHERE_CONFIG.final.durationMs}
                        guesses={finalRound.guesses}
                        lastDirection={lastDirection}
                        feedback={finalFeedback}
                        onGuess={submitFinalGuess}
                        revealPrice={revealPrice}
                      />
                    ) : null}

                    {showIntroMessage ? (
                      <Box sx={{ py: 4, textAlign: 'center', color: 'white' }}>
                        Lancez une partie pour découvrir de nouveaux véhicules.
                      </Box>
                    ) : null}
                  </GameShell>
                )}
              </Box>

              {/* ✅ Panel stats fixe, et collé à droite sans créer un “couloir” */}
              <Box
                sx={{
                  flex: '0 0 360px',
                  width: { xs: '100%', md: 360 },
                  maxWidth: 420,
                  ml: { md: 'auto' },
                }}
              >
                <ScorePanel stats={stats} />
              </Box>
            </Box>
          </Box>
        </GameBackdrop>
      </Box>

      {brandsError ? (
        <Alert severity="warning" sx={{ width: '100%', maxWidth: 420 }}>
          {brandsError}
        </Alert>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ width: '100%', maxWidth: 420 }}>
          {error}
        </Alert>
      ) : null}
    </Stack>
  )
}

export default JusteEnchereGame
